import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { Button, TextField, MenuItem } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import moment from "moment";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import MyLocationRoundedIcon from "@mui/icons-material/MyLocationRounded";
import useAxios from "../../helpers/Hooks/useAxios";
import { URLS } from "../../helpers/URLS";
import {
    getBrowserLocation, locationBody, locationProblem, formatLocation, mapLink,
} from "../../helpers/geolocation";
import { toast } from "react-toastify";
import "../../assets/scss/tables.scss";

/* Work From Home is a leave type in this system, so both actions post the
   same request — `mode` only decides which type is preselected and how the
   popup reads. */
const WFH_NAME = "work from home";

/* `leave` turns this into an edit: the existing request is prefilled and the
   save goes to updateLeaveRequest instead of creating a new one.

   `mode` decides what the type dropdown offers:
     "leave" every type except Work From Home (it has its own button)
     "wfh"   only Work From Home
     "any"   everything, for the calendar — you click a day and then say what
             you want it to be, rather than deciding before you get here.

   `date` prefills both ends of the range, so clicking the 24th asks for the
   24th without anyone retyping it. */
const ApplyModal = ({ show, mode = "leave", leave = null, date = null, onClose, onSaved }) => {

    const { fetchData } = useAxios();

    const [types, setTypes] = useState([]);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        leaveType: "",
        startDate: new Date(),
        endDate: new Date(),
        remarks: "",
    });

    const isWfh = mode === "wfh";
    const isAny = mode === "any";
    const isEdit = Boolean(leave?.leaveRequestID);

    /* Whether the type actually chosen is Work From Home. In "any" mode that
       is not known until the dropdown is used, so it is read from the
       selection rather than from the mode — which gives the same answer as
       before in the other two modes. */
    const selectedIsWfh = String(
        types.find((t) => t._id === form.leaveType)?.name || "").toLowerCase() === WFH_NAME;

    /* A work from home request has to say where from, so the location is a
       field on the form rather than something collected quietly on submit. */
    const needsLocation = selectedIsWfh && !isEdit;

    /* Arrived from a calendar day: that day IS the request, so the dates are
       shown but not editable. Letting them be changed here would mean the
       popup could quietly end up about a different day than the one that was
       clicked. The toolbar buttons are still the way to ask for a range. */
    const datesLocked = Boolean(date) && !isEdit;

    const [place, setPlace] = useState(null);
    const [locating, setLocating] = useState(false);

    const detectLocation = async () => {
        setLocating(true);
        const result = await getBrowserLocation();
        setLocating(false);
        setPlace(result);
        return result;
    };

    useEffect(() => {
        if (!show) return;

        const load = async () => {
            const resp = await fetchData({ url: URLS.getLeaveTypes, method: "get" });
            const list = resp?.data || [];
            setTypes(list);

            if (isEdit) {
                setForm({
                    leaveType: leave.leaveType?._id || leave.leaveType || "",
                    startDate: leave.startDate ? new Date(leave.startDate) : new Date(),
                    endDate: leave.endDate ? new Date(leave.endDate) : new Date(),
                    remarks: leave.remarks || "",
                });
                return;
            }

            const wfh = list.find((t) => String(t.name || "").toLowerCase() === WFH_NAME);
            const first = list.find((t) => String(t.name || "").toLowerCase() !== WFH_NAME);

            /* Opened from a calendar day: that day is the request, and no type
               is assumed — the whole point of arriving this way is to choose
               one. */
            const when = date ? new Date(date) : new Date();

            setForm({
                leaveType: isAny ? "" : ((isWfh ? wfh?._id : first?._id) || ""),
                startDate: when,
                endDate: when,
                remarks: "",
            });
        };

        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, mode, leave, date]);

    useEffect(() => {
        if (!show || !needsLocation) {
            setPlace(null);
            return;
        }
        /* asked for on open so the prompt is answered while the form is being
           filled in, rather than at the moment of sending */
        detectLocation();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, needsLocation]);

    const days = Math.max(
        moment(form.endDate).startOf("day").diff(moment(form.startDate).startOf("day"), "days") + 1, 0);

    const invalid = !form.leaveType || !form.remarks.trim() || days < 1
        || (needsLocation && !(place && place.ok));

    const submit = async (e) => {
        e.preventDefault();
        if (invalid || saving) return;

        setSaving(true);

        const body = {
            leaveType: form.leaveType,
            startDate: form.startDate,
            endDate: form.endDate,
            remarks: form.remarks.trim(),
            ...locationBody(needsLocation ? place : null),
        };

        const resp = await fetchData({
            url: isEdit
                ? `${URLS.updateLeaveRequest}/${leave.leaveRequestID}`
                : URLS.createLeaveRequest,
            method: "post",
            body,
        });
        setSaving(false);

        if (resp?.success === false) {
            toast.error(resp?.message || "Could not send the request");
            return;
        }

        toast.success(isEdit
            ? "Request updated"
            : isWfh
                ? "Work from home request sent to your manager, with your location"
                : "Leave request sent to your manager");
        if (onSaved) onSaved();
        onClose();
    };

    /* on a WFH request the type is fixed; on a leave request the WFH type is
       not offered, since it has its own button */
    const options = isEdit || isAny
        ? types
        : isWfh
            ? types.filter((t) => String(t.name || "").toLowerCase() === WFH_NAME)
            : types.filter((t) => String(t.name || "").toLowerCase() !== WFH_NAME);

    return (
        <Modal show={show} onHide={onClose} backdrop="static" className="wh-modal">
            <form onSubmit={submit}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {isEdit ? "Update Request"
                            : isWfh ? "Apply for Work From Home"
                                : isAny ? "Apply for Leave or Work From Home"
                                    : "Apply for Leave"}
                        {isAny && date && (
                            <span className="wh-dialog-sub">
                                {moment(date).format("ddd, DD MMM YYYY")}
                            </span>
                        )}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div className="att-apply-note">
                        <span className="att-apply-note-icon">
                            {selectedIsWfh ? <HomeWorkOutlinedIcon /> : <EventBusyOutlinedIcon />}
                        </span>
                        <p>{isEdit
                            ? "Your manager sees the updated request when you save."
                            : needsLocation
                                ? "Sent to your manager for approval, along with where you will be working."
                                : "Sent to your manager for approval — approved days show on your attendance."}</p>
                    </div>

                    <div className="wh-form-grid" style={{ marginTop: 14 }}>
                        <div className="wh-form-full">
                            <TextField
                                select
                                size="small"
                                fullWidth
                                label={isWfh || isAny ? "Request type" : "Leave type"}
                                value={form.leaveType}
                                onChange={(e) => setForm({ ...form, leaveType: e.target.value })}
                                helperText={isAny && !form.leaveType ? "Pick what you are applying for" : " "}
                                error={isAny && !form.leaveType}
                                disabled={!isEdit && isWfh && options.length <= 1}
                            >
                                {options.map((t) => (
                                    <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>
                                ))}
                            </TextField>
                        </div>

                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DesktopDatePicker
                                label="From"
                                inputFormat="DD/MM/YYYY"
                                disabled={datesLocked}
                                value={dayjs(form.startDate)}
                                onChange={(date) => {
                                    const start = date ? date.toDate() : new Date();
                                    /* keep the range valid rather than letting it invert */
                                    setForm((prev) => ({
                                        ...prev,
                                        startDate: start,
                                        endDate: prev.endDate < start ? start : prev.endDate,
                                    }));
                                }}
                                renderInput={(params) => <TextField {...params} size="small" fullWidth helperText=" " />}
                            />
                        </LocalizationProvider>

                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DesktopDatePicker
                                label="To"
                                inputFormat="DD/MM/YYYY"
                                disabled={datesLocked}
                                minDate={dayjs(form.startDate)}
                                value={dayjs(form.endDate)}
                                onChange={(date) => setForm({ ...form, endDate: date ? date.toDate() : new Date() })}
                                renderInput={(params) => <TextField {...params} size="small" fullWidth helperText=" " />}
                            />
                        </LocalizationProvider>

                        {needsLocation && (
                            <div className="wh-form-full">
                                <div className={`att-loc-field${place && place.ok ? " is-set" : " is-missing"}`}>
                                    <span className="att-loc-icon"><PlaceOutlinedIcon /></span>

                                    <span className="att-loc-text">
                                        <span className="att-loc-label">Location <b>*</b></span>
                                        {locating
                                            ? <em>Getting your location…</em>
                                            : place && place.ok
                                                ? <a href={mapLink(place)} target="_blank" rel="noopener noreferrer">
                                                    {formatLocation(place)}
                                                    {Number.isFinite(place.accuracy)
                                                        ? ` (±${Math.round(place.accuracy)}m)` : ""}
                                                </a>
                                                : <em>{place ? locationProblem(place) : "Not captured yet"}</em>}
                                    </span>

                                    <Button size="small" variant="outlined" disabled={locating}
                                        startIcon={<MyLocationRoundedIcon />}
                                        onClick={detectLocation}>
                                        {place && place.ok ? "Update" : "Detect"}
                                    </Button>
                                </div>

                                <p className="att-loc-help">
                                    {place && place.ok
                                        ? "Your manager sees where you will be working."
                                        : "Required — allow location access in your browser, then press Detect."}
                                </p>
                            </div>
                        )}

                        <div className="wh-form-full">
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                size="small"
                                label="Reason"
                                placeholder={isWfh
                                    ? "Why you need to work from home"
                                    : "Why you need the time off"}
                                value={form.remarks}
                                onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                                helperText={form.remarks.trim() ? " " : "A reason is required"}
                                error={!form.remarks.trim()}
                            />
                        </div>
                    </div>

                    <p className="att-apply-days">
                        <b>{days}</b> {days === 1 ? "day" : "days"} requested
                        {datesLocked && (
                            <em className="att-apply-locked">
                                the day you picked on the calendar
                            </em>
                        )}
                    </p>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="contained" color="inherit" onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button type="submit" variant="contained" disabled={invalid || saving}>
                        {saving ? "Saving…" : isEdit ? "Update Request" : "Send Request"}
                    </Button>
                </Modal.Footer>
            </form>
        </Modal>
    );
};

export default ApplyModal;
